import { z } from 'zod';

import { IssuedStorageObjectKeySchema } from './storage.js';

/**
 * @aiPurpose per-RussiaRequest file capacity caps (#1546). 1:1 mirror of
 *   the China-side request-file caps (`REQUEST_FILE_*`): `MAX_BYTES`
 *   mirrors the backend `request-attachment` storage-scope ceiling so the
 *   wire layer rejects oversize uploads before issuing a presigned URL;
 *   `MAX_PER_REQUEST` is the per-RussiaRequest aggregate live-row cap
 *   enforced at file-bind time.
 */
export const RUSSIA_REQUEST_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const RUSSIA_REQUEST_FILE_MAX_PER_REQUEST = 20;
export const RUSSIA_REQUEST_FILE_NAME_MAX = 256;

/**
 * @aiPurpose allowlist of MIME content types accepted for a RussiaRequest
 *   file. Identical to the China-side `REQUEST_FILE_ALLOWED_MIME`: common
 *   images (JPEG/PNG/WebP), PDFs, and Office documents (DOC/DOCX/XLS/XLSX).
 * @aiSecurity load-bearing — the backend mirrors this list when
 *   `scope === 'request-attachment'`. The presigned upload URL also binds
 *   Content-Type so a forged upload is rejected at PUT time as defense in
 *   depth.
 */
export const RUSSIA_REQUEST_FILE_ALLOWED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;
export type RussiaRequestFileMime = (typeof RUSSIA_REQUEST_FILE_ALLOWED_MIME)[number];
export const RussiaRequestFileMimeSchema = z.enum(RUSSIA_REQUEST_FILE_ALLOWED_MIME);

const RussiaRequestFileNameSchema = z
  .string()
  .min(1)
  .max(RUSSIA_REQUEST_FILE_NAME_MAX)
  .refine((s) => s.trim().length > 0, 'fileName must not be whitespace-only')
  // Block path-traversal payloads at the wire layer. The persistence
  // column stores display text only; segments like `..` or `/` should
  // never appear here.
  .refine(
    (s) => !s.includes('/') && !s.includes('\\') && !s.includes('..'),
    'fileName must not contain path separators',
  );

/**
 * @aiPurpose wire projection of a RussiaRequest file row returned by the
 *   RF files read surface. Mirror of the China-side `RequestFileSchema`.
 * @aiInvariant only live (non-soft-deleted) rows are returned.
 * @aiPii `fileName` is user-supplied display text. The server-side
 *   `objectKey` is INTENTIONALLY absent from this read model — the
 *   download path issues a presigned URL keyed on the file id, so the raw
 *   storage key never leaves the backend.
 */
export const RussiaRequestFileSchema = z
  .object({
    id: z.string().uuid(),
    fileName: z.string().min(1).max(RUSSIA_REQUEST_FILE_NAME_MAX),
    contentType: z.string(),
    contentLength: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type RussiaRequestFile = z.infer<typeof RussiaRequestFileSchema>;

/**
 * @aiPurpose request body for the RussiaRequest file-bind endpoint. The
 *   client has ALREADY exchanged a presigned upload URL via
 *   `/storage/upload-url` with `scope = 'request-attachment'` and PUT the
 *   bytes; this endpoint records the metadata row that binds the issued
 *   key to the RussiaRequest. Mirror of the China-side
 *   `RequestFileCreateInputSchema`.
 * @aiSecurity the service re-validates that:
 *     1. `objectKey` was issued to the caller (the embedded userId
 *        segment equals the JWT subject);
 *     2. `contentType` is in `RUSSIA_REQUEST_FILE_ALLOWED_MIME`;
 *     3. `contentLength` ≤ `RUSSIA_REQUEST_FILE_MAX_BYTES`;
 *     4. the live-row count for the RussiaRequest is below
 *        `RUSSIA_REQUEST_FILE_MAX_PER_REQUEST`;
 *     5. the `objectKey` is not already bound to another RussiaRequest.
 */
export const RussiaRequestFileCreateInputSchema = z
  .object({
    objectKey: IssuedStorageObjectKeySchema,
    fileName: RussiaRequestFileNameSchema,
    contentType: RussiaRequestFileMimeSchema,
    contentLength: z.number().int().positive().max(RUSSIA_REQUEST_FILE_MAX_BYTES),
  })
  .strict();
export type RussiaRequestFileCreateInput = z.infer<typeof RussiaRequestFileCreateInputSchema>;
